import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import { api, endpoints } from '../config/api';

interface GroupStat {
    tourId: string;
    tourName: string;
    totalParticipants: number;
    sessionsCount: number;
    lastSessionDate: string;
    averageParticipants: number;
}

const GroupStats: React.FC = () => {
    const [stats, setStats] = useState<GroupStat[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const response = await api.get(endpoints.groupStats);
            if (response.data && response.data.success) {
                setStats(response.data.data);
            } else {
                setError('Failed to load statistics');
            }
        } catch (err) {
            console.error('Error fetching group stats:', err);
            setError('Error loading statistics. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 4, color: 'primary.main', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <GroupsIcon sx={{ mr: 2, fontSize: 40 }} />
                Statistiche Gruppi
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : (
                <>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Typography color="text.secondary" gutterBottom>
                                        Totale Partecipanti
                                    </Typography>
                                    <Typography variant="h3" component="div" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                                        {stats.reduce((sum, stat) => sum + stat.totalParticipants, 0)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Typography color="text.secondary" gutterBottom>
                                        Sessioni Totali
                                    </Typography>
                                    <Typography variant="h3" component="div" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                                        {stats.reduce((sum, stat) => sum + stat.sessionsCount, 0)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Typography color="text.secondary" gutterBottom>
                                        Media Partecipanti/Sessione
                                    </Typography>
                                    <Typography variant="h3" component="div" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                                        {stats.length > 0
                                            ? Math.round(stats.reduce((sum, stat) => sum + stat.totalParticipants, 0) / stats.reduce((sum, stat) => sum + stat.sessionsCount, 0))
                                            : 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tour</TableCell>
                                    <TableCell align="right">Sessioni</TableCell>
                                    <TableCell align="right">Totale Partecipanti</TableCell>
                                    <TableCell align="right">Media Partecipanti</TableCell>
                                    <TableCell align="right">Ultima Sessione</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stats.map((row) => (
                                    <TableRow key={row.tourId} hover>
                                        <TableCell component="th" scope="row">
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {row.tourName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                                {row.sessionsCount}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                                {row.totalParticipants}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {Math.round(row.averageParticipants * 10) / 10}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatDate(row.lastSessionDate)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {stats.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            Nessun dato disponibile
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

export default GroupStats;
